import LocalCache from "src/shared/LocalCache";
import EligibilityService from "src/shared/EligibilityService";
import TrackingRepository from "src/shared/TrackingRepository";
import TargetingService, { TargetingDecision } from "src/host/TargetingService";
import { Events, EventService } from "src/shared/EventService";

interface AskMeLaterDecision {
    workflow: string;
    target: boolean;
    continueTargeting: boolean;
}

export default class Targeter {
    private readonly targetingService: TargetingService;
    private readonly eligibilityService: EligibilityService;
    private readonly cache: LocalCache;
    private readonly repo: TrackingRepository;
    private readonly eventService: EventService;

    constructor(
        targetingService: TargetingService,
        eligibilityService: EligibilityService,
        cache: LocalCache,
        repo: TrackingRepository,
        eventService: EventService,
    ) {
        this.targetingService = targetingService;
        this.eligibilityService = eligibilityService;
        this.cache = cache;
        this.repo = repo;
        this.eventService = eventService;
    }

    public getAskMeLaterDecision(clear = false): AskMeLaterDecision {
        // No ask me later data, don't target but continue with other targeting logic
        const aml = this.repo.getAskMeLater(clear);
        if (!aml) {
            return {
                workflow: '',
                target: false,
                continueTargeting: true
            };
        }

        // Ask me later time has exceeded, target the user with the survey
        if (Date.now() >= aml.time) {
            this.repo.customerWasTargeted(aml.workflow);

            const workflow = aml.workflow.startsWith("vibes:") ? aml.workflow.substr(6) : aml.workflow;

            return {
                workflow,
                target: true,
                continueTargeting: true
            };
        }

        // Ask me later time has not exceeded, do not target and do not continue other targeting logic
        return {
            workflow: '',
            target: false,
            continueTargeting: false
        };
    }

    public async shouldTarget(): Promise<[string | false, boolean]> {
        const cacheKey = 'vibes-cache:targeting';
        const amlDecision = this.getAskMeLaterDecision(true);
        if (amlDecision.target) {
            return [amlDecision.workflow, true];
        } else if (!amlDecision.continueTargeting) {
            return [false, false];
        }

        let fromCache = false;
        // check the cache
        let targetingDecision = this.cache.getJson<TargetingDecision>(cacheKey);
        if (targetingDecision) {
            fromCache = true;
        }

        // if cache-miss, hit the targeting api
        if (!targetingDecision) {
            const response = await this.targetingService.getTargetingInfo();
            targetingDecision = { name: response.name, sample_rate: response.sample_rate };
            this.cache.setJson(cacheKey, targetingDecision, response.ttl);
        }

        // if the workflow name or sample rate is missing, return false;
        if (!targetingDecision.name  || !targetingDecision.sample_rate) {
            return [false, false];
        }

        // sample to see if user should be targeted
        const shouldTarget = Math.random() <= targetingDecision.sample_rate;

        if (!shouldTarget) {
            const shouldSample = Math.random() <= .01; // Only emit this metric for 1% of page views.
            if (shouldSample) {
                await this.eventService.saveEvent(Events.refusedToTarget, targetingDecision.name);
            }
            return [false, false];
        }

        // if we're targeting, update the cache to a no-target decision for five minutes
        this.cache.setJson(cacheKey, { name: null, sample_rate: 0 }, 60 * 5 * 1000);

        const eligible = await this.eligibilityService.isEligible();

        if (!eligible) {
            return [false, false];
        }

        const targetingEvent = fromCache ? Events.targetingDecisionFromCache : Events.targetingDecisionFromAPI;
        await this.eventService.saveEvent(targetingEvent, targetingDecision.name);

        return [targetingDecision.name, false];
    }
}